import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, HelpCircle, ShieldCheck, XCircle } from "lucide-react";

import {
  APIError,
  answerDecision,
  listAccessTokens,
  listDecisions,
  resolveDecision,
  type AccessToken,
  type PendingDecision,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Page";

function prettyJSON(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// ApprovalPage is the minimal, OAuth-consent-style page a notification (e.g. a
// Telegram card) links to: it shows ONLY the held request's context + Approve /
// Reject — no app shell, no tabs. Ticket+project addressed so it can load and
// resolve that single decision. Reuses the project-scoped decisions endpoints.
export function ApprovalPage() {
  const { projectId = "", ticket = "" } = useParams();
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  const [canResolve, setCanResolve] = useState(false);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [result, setResult] = useState<"approved" | "rejected" | "answered" | null>(null);
  const [answer, setAnswer] = useState("");

  const callerName = (id?: string) => (!id ? "Unknown caller" : tokens.find((t) => t.id === id)?.name ?? id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, toks] = await Promise.all([
        listDecisions(projectId),
        listAccessTokens(projectId).catch(() => [] as AccessToken[]),
      ]);
      setDecision(r.decisions.find((d) => d.ticket_id === ticket) ?? null);
      setCanResolve(r.canResolve);
      setTokens(toks);
      setErr(null);
    } catch (e) {
      if (e instanceof APIError && e.status === 403) setErr("forbidden");
      else setErr(humanizeError(e, "Couldn't load this approval"));
    } finally {
      setLoading(false);
    }
  }, [projectId, ticket]);

  useEffect(() => {
    void load();
  }, [load]);

  const resolve = useCallback(
    async (approve: boolean) => {
      setActing(true);
      try {
        await resolveDecision(projectId, ticket, approve);
        setResult(approve ? "approved" : "rejected");
      } catch (e) {
        if (e instanceof APIError && e.status === 409) {
          // already resolved/expired between load and click
          setDecision(null);
          setErr(null);
        } else {
          setErr(humanizeError(e, "Couldn't record your decision"));
        }
      } finally {
        setActing(false);
      }
    },
    [projectId, ticket],
  );

  const submitAnswer = useCallback(async () => {
    const a = answer.trim();
    if (!a) return;
    setActing(true);
    try {
      await answerDecision(projectId, ticket, a);
      setResult("answered");
    } catch (e) {
      if (e instanceof APIError && e.status === 409) {
        setDecision(null);
        setErr(null);
      } else {
        setErr(humanizeError(e, "Couldn't send your answer"));
      }
    } finally {
      setActing(false);
    }
  }, [answer, projectId, ticket]);

  const isQuestion = decision?.kind === "question";

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md rounded-xl border bg-background p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {isQuestion ? (
            <>
              <HelpCircle className="h-5 w-5 text-primary" />
              Agent question
            </>
          ) : (
            <>
              <ShieldCheck className="h-5 w-5 text-primary" />
              CodeBridge approval
            </>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : result ? (
          <Resolved result={result} />
        ) : err === "forbidden" ? (
          <Centered icon={<XCircle className="h-8 w-8 text-destructive" />}>
            {isQuestion
              ? "You don't have permission to answer questions on this agent. Ask a project admin."
              : "You don't have permission to resolve approvals on this agent. Ask a project admin."}
          </Centered>
        ) : err ? (
          <div className="space-y-4">
            <p className="text-sm text-destructive">{err}</p>
            <Button variant="outline" onClick={() => void load()}>
              Retry
            </Button>
          </div>
        ) : !decision ? (
          <Centered icon={<CheckCircle2 className="h-8 w-8 text-muted-foreground" />}>
            This request is no longer pending — it was already handled or it expired.
          </Centered>
        ) : isQuestion ? (
          <div className="space-y-5">
            <div>
              <h1 className="text-lg font-semibold">Answer the agent's question</h1>
              <p className="text-sm text-muted-foreground">
                The agent couldn't find this and asked for your help. Your reply is sent back to it.
              </p>
            </div>
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <div className="mb-1 text-xs text-muted-foreground">
                From {callerName(decision.principal)} · {new Date(decision.created_at).toLocaleString()}
              </div>
              <div className="whitespace-pre-wrap">{decision.question || decision.reason}</div>
            </div>
            {canResolve ? (
              <div className="space-y-3">
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Type the answer to teach the agent…"
                  rows={4}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
                <Button className="w-full" disabled={acting || !answer.trim()} onClick={() => void submitAnswer()}>
                  {acting && <Spinner />} Send answer
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                You need admin access on this agent to answer. Ask a project admin.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <h1 className="text-lg font-semibold">Approve this tool call?</h1>
              <p className="text-sm text-muted-foreground">An agent's action is paused waiting on your decision.</p>
            </div>
            <dl className="space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
              <Row label="Tool" value={<span className="font-mono">{decision.tool}</span>} />
              <Row label="Caller" value={callerName(decision.principal)} />
              {decision.reason && <Row label="Reason" value={decision.reason} />}
              <Row label="Requested" value={new Date(decision.created_at).toLocaleString()} />
            </dl>
            {decision.args_preview && (
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Arguments</div>
                <pre className="max-h-48 overflow-auto rounded border bg-muted/30 p-2 text-[11px]">
                  {prettyJSON(decision.args_preview)}
                </pre>
              </div>
            )}
            {canResolve ? (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 text-destructive focus:text-destructive"
                  disabled={acting}
                  onClick={() => void resolve(false)}
                >
                  Reject
                </Button>
                <Button className="flex-1" disabled={acting} onClick={() => void resolve(true)}>
                  {acting && <Spinner />} Approve
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                You need admin access on this agent to approve or reject. Ask a project admin.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right">{value}</dd>
    </div>
  );
}

function Centered({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center text-sm text-muted-foreground">
      {icon}
      <p>{children}</p>
    </div>
  );
}

function Resolved({ result }: { result: "approved" | "rejected" | "answered" }) {
  if (result === "answered") {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-success" />
        <p className="text-base font-medium">Answer sent</p>
        <p className="text-sm text-muted-foreground">
          The agent has your answer and will remember it. You can close this page.
        </p>
      </div>
    );
  }
  const approved = result === "approved";
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      {approved ? (
        <CheckCircle2 className="h-10 w-10 text-success" />
      ) : (
        <XCircle className="h-10 w-10 text-destructive" />
      )}
      <p className="text-base font-medium">{approved ? "Approved" : "Rejected"}</p>
      <p className="text-sm text-muted-foreground">
        {approved
          ? "The agent can proceed with this call. You can close this page."
          : "The agent's call was blocked. You can close this page."}
      </p>
    </div>
  );
}
